import { useMemo, useState } from 'react';
import type { KioskMember } from './types';

// Mirrors backend/src/domain/types.ts's SAMMELGAST_MEMBER_ID (no shared
// types package between frontend/backend, per project convention -- kept in
// sync manually, same as the backend's own migrate-v0.11.mjs duplicate).
// The reserved row's empty `divisionIds` already keeps it out of the
// DEFAULT (no-search) picker view; this excludes it from the free-text
// SEARCH path too (ADR-0002 v0.11 amendment, "the Developer should still
// verify the picker's search path can't surface it") -- defensive, since an
// operator typing "Sammelgast" on an ordinary event should never be able to
// select it as if it were a real guest.
const SAMMELGAST_MEMBER_ID = 'sammelgast';

interface Props {
  eventDivisionId: string;
  members: KioskMember[];
  onSelect: (member: KioskMember) => void;
}

function memberLabel(m: KioskMember): string {
  return m.suffix ? `${m.firstName} ${m.lastName} (${m.suffix})` : `${m.firstName} ${m.lastName}`;
}

/**
 * FR-19/FR-19a/FR-58/FR-62/AC-2/AC-20/AC-39/AC-41: event-division members by
 * default (a member appears if ANY of their divisions matches), searchable
 * across all members, each division-list subtitle shows every division the
 * member belongs to. Members already arrive first-name sorted from the
 * server; filtering preserves that order.
 */
export default function MemberPicker({ eventDivisionId, members, onSelect }: Props) {
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) {
      return members.filter((m) => m.divisionIds.includes(eventDivisionId));
    }
    return members.filter(
      (m) =>
        m.memberId !== SAMMELGAST_MEMBER_ID &&
        `${m.firstName} ${m.lastName}`.toLowerCase().includes(q),
    );
  }, [members, eventDivisionId, search]);

  return (
    <div>
      <input
        className="kiosk-search"
        type="search"
        placeholder="Mitglied suchen (Vor- oder Nachname)…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Mitglied suchen"
      />
      <div className="kiosk-grid">
        {visible.map((m) => (
          <button key={m.memberId} className="kiosk-tile" onClick={() => onSelect(m)}>
            <span>{memberLabel(m)}</span>
            <span className="subtitle">{m.divisionNames.join(', ')}</span>
          </button>
        ))}
        {visible.length === 0 && <p>Keine Mitglieder gefunden.</p>}
      </div>
    </div>
  );
}
